import React, { useEffect, useRef, useState } from "react";
import {
  Container,
  Badge,
  UncontrolledTooltip,
  Col,
  Row,
  Button,
  Card,
} from "reactstrap";
import ReactBsTable from "components/Tables/ReactBsTable";
import DynamicFormModal from "components/Modal/Form";
import { productTypeFormFields } from "variables/formFields";
import { DEFAULT_STATUS_LABELS } from "variables/constants";
import { showAlert } from "components/Common/commonFunctions";
import { PRODUCT_TYPE_OPTIONS } from "variables/constants";
import SearchableDropdown from "components/DropDown/SearchableDropdown";
import { getProductTypeList } from "actions/productType";
import NotificationAlert from "react-notification-alert";
import { createProductType } from "actions/productType";
import { updateProductType } from "actions/productType";
import { mapValuesToLabels } from "components/DropDown/MapLabelsToValues";
import { STATUS_OPTIONS } from "variables/constants";
import { getProductTypeDetails } from "actions/productType";
import { deleteProductType } from "actions/productType";

const sortFieldMap = {
  name: "Name",
  status: "Status",
};

const ProductTypes = () => {
  const [data, setData] = useState({
    data: [],
    totalRecords: 0,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [showLoader, setshowLoader] = useState(false);
  const [formState, setFormState] = useState({
    values: { status: true },
    errors: {},
  });
  const [filters, setFilters] = useState({
    product_type: "",
    status: "",
    filterParams: [],
  });
  const [sortConfig, setSortConfig] = useState({
    dataField: "",
    sortField: "",
    sortOrder: "",
  });
  const notificationAlertRef = useRef(null);

  const isEditMode = !!selected; // Check selected row is Edit mode or not.

  //===> notify function is used for to show notification on create and update the productType.
  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  //===> Product Type List Api Call
  const getProductTypeListData = async () => {
    try {
      setshowLoader(true);
      const res = await getProductTypeList(
        pageNum,
        filters.filterParams,
        "",
        sortConfig.sortField,
        sortConfig.sortOrder,
      );
      if (res.code === 200) {
        setshowLoader(false);
        setData({
          data: res?.data?.ProductType || [],
          totalRecords: res?.data.totalRecords,
        });
      } else {
        setshowLoader(false);
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      setshowLoader(false);
    }
  };

  // COMBINED useEffect - removed separate sort useEffect
  useEffect(() => {
    getProductTypeListData();
  }, [pageNum, filters.filterParams, sortConfig.sortField, sortConfig.sortOrder]);

  // REMOVED the separate sort useEffect - now combined with above
  // useEffect(() => {
  //   if (sortConfig.sortField || sortConfig.sortOrder) {
  //     getProductTypeListData();
  //   }
  // }, [sortConfig.sortField, sortConfig.sortOrder]);

  //========================

  const handleSortChange = (dataField, sortOrder) => {
    const mappedSortField = sortFieldMap[dataField] || dataField;
    setSortConfig({ dataField, sortField: mappedSortField, sortOrder });
    // setPageNum(1);
  };

  //==> handleChange function is used for set form values a state.
  const handleChange = (e) => {
    const name = e?.target?.name || e?.name;
    const value = e?.target?.value ?? e?.value;

    setFormState((prev) => ({
      values: { ...prev.values, [name]: value },
      errors: { ...prev.errors, [name]: "" },
    }));
  };

  //==> validateForm function is used for check form fields validate or not.
  const validateForm = () => {
    const errors = {};
    productTypeFormFields.forEach((f) => {
      if (f.required && !formState.values[f.name]) {
        errors[f.name] = `${f.label} is required`;
      }
    });
    setFormState((p) => ({ ...p, errors }));
    return Object.keys(errors).length === 0;
  };

  //==> handleSubmit function is used for create and update the product Type.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setshowLoader(true);
    try {
      const payload = {
        name: formState.values.name,
        status: formState.values.status ? "ACTIVE" : "INACTIVE",
      };
      const res = !isEditMode
        ? await createProductType(payload)
        : await updateProductType(selected?.ID, payload);
      if (res.code === 200) {
        setshowLoader(false);
        notify("success", res?.message);
        getProductTypeListData();
        setModalOpen(false);
        setSelected(null);
      } else {
        setshowLoader(false);
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      setshowLoader(false);
      notify("danger", "Something went wrong");
    }
  };

  //=> confirmStatus function is used for update the Product type status.
  const confirmStatus = async (data) => {
    try {
      let payLoad = {
        name: data.name,
        status: data.status === "ACTIVE" ? "INACTIVE" : "ACTIVE",
      };
      const res = await updateProductType(data?.ID, payLoad);
      if (res.code === 200) {
        notify("success", res?.message);
        getProductTypeListData();
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {}
  };

  //=> handleStatusToggle function is used for to show Active and Inactive status modal.
  const handleStatusToggle = async (row) => {
    const isActive = row.status === "ACTIVE";
    const res = await getProductTypeDetails(row?.ID);
    let data = res?.data;
    showAlert({
      title: `Are you sure you want to ${
        isActive ? "inactivate" : "activate"
      } this product type?`,
      message: `Product Type "${row.name}" will be ${
        isActive ? "inactive" : "active"
      }.`,
      type: "warning",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: () => confirmStatus(data),
    });
  };

  //==> handleDelete function is used for to show delete modal.
  const handleDelete = (row) => {
    showAlert({
      title: "Delete Product Type?",
      message: `Product Type "${row.name}" will be permanently deleted.`,
      type: "warning",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: () => deleteProductTypeById(row),
    });
  };

  //==> deleteProductTypeById function is used for delete the product type by Id.
  const deleteProductTypeById = async (row) => {
    try {
      const res = await deleteProductType(row?.ID);
      if (res.code === 200) {
        notify("success", res?.message);
        getProductTypeListData();
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {}
  };

  // ===> columns is used for to show the columns on Product Type List.
  const columns = [
    // {
    //   text: "Sr. No.",
    //   dataField: "srNo",
    //   // formatter: (_, __, i) => i + 1,
    //   formatter: (_, __, rowIndex) => (pageNum - 1) * 10 + rowIndex + 1,
    // },
    {
      dataField: "name",
      text: "Product Type Name",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (cell) => (
        <Badge color={cell === "ACTIVE" ? "success" : "danger"}>{cell}</Badge>
      ),
      // sort: true,
      // sortCaret: (order) => {
      //   if (!order) {
      //     return (
      //       <span className="sort-caret" style={{ marginLeft: "8px" }}>
      //         <i className="fa fa-sort" />
      //       </span>
      //     );
      //   }
      //   return order === "asc" ? (
      //     <span className="sort-caret" style={{ marginLeft: "8px" }}>
      //       <i className="fa fa-sort-up" />
      //     </span>
      //   ) : (
      //     <span className="sort-caret" style={{ marginLeft: "8px" }}>
      //       <i className="fa fa-sort-down" />
      //     </span>
      //   );
      // },
    },
    {
      text: "Actions",
      dataField: "actions",
      formatter: (_, row) => {
        const rowId = row.ID;
        return (
          <>
            {/* Edit */}
            <a
              id={`edit${rowId}`}
              className="table-action table-action-info"
              onClick={() => {
                setSelected(row);
                setFormState({
                  values: {
                    ...row,
                    status: row.status === "ACTIVE",
                  },
                  errors: {},
                });
                setModalOpen(true);
              }}
            >
              <i className="fas fa-edit" />
            </a>
            <UncontrolledTooltip target={`edit${rowId}`}>
              Edit
            </UncontrolledTooltip>

            {/* Delete */}
            <a
              id={`delete${rowId}`}
              className="table-action table-action-delete"
              onClick={() => handleDelete(row)}
            >
              <i className="fas fa-trash" />
            </a>
            <UncontrolledTooltip target={`delete${rowId}`}>
              Delete
            </UncontrolledTooltip>
            {/* Active / Inactive */}
            <a
              className={`table-action table-action-delete ${
                row.status === "ACTIVE" ? "status-active" : "status-inactive"
              }`}
              id={`toggle${rowId}`}
              onClick={(e) => {
                e.stopPropagation();
                handleStatusToggle(row);
              }}
            >
              {row.status === "ACTIVE" ? (
                <i className="fa-solid fa-xmark" />
              ) : (
                <i className="fa-solid fa-check" />
              )}
            </a>
            <UncontrolledTooltip target={`toggle${rowId}`}>
              {row.status === "ACTIVE" ? "Mark Inactive" : "Mark Active"}
            </UncontrolledTooltip>
          </>
        );
      },
    },
  ];

  //==> buildFilterParams function is used for make filterJson array.
  const buildFilterParams = () => {
    const filterParams = [];

    if (filters?.status !== "") {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filters?.status],
      });
    }

    if (filters?.product_type !== "") {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filters?.product_type],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  //===> handleFilterChange function is used for set filters values in a state.
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  //==> isAnyFilterApplied function is used for to check is any filter is applied or not to avoid api call.
  const isAnyFilterApplied = (filters) => {
    return Object.entries(filters).some(([key, value]) => {
      if (key === "filterParams") {
        return Array.isArray(value) && value.length > 0;
      }
      return value !== "" && value !== null && value !== undefined;
    });
  };

  //==> handleSearch function is used for search the filters values in a list.
  const handleSearch = () => {
    if (!isAnyFilterApplied(filters)) return;
    const newfilterParams = buildFilterParams();
    setFilters({
      ...filters,
      filterParams: newfilterParams,
    });
    if (pageNum !== 1) setPageNum(1);
    // setData(result);
  };

  //==> handleReset function is used for reset filters values.
  const handleReset = () => {
    if (!isAnyFilterApplied(filters)) return;
    setFilters({
      product_type: "",
      status: "",
      filterParams: [],
    });
    // setData(initialData);
  };

  //==> filterSection is used for show filters on list.
  const filterSection = () => {
    return (
      <>
        <Row className="align-items-center">
          <Col md={2}>
            {/* <SearchableDropdown
              options={PRODUCT_TYPE_OPTIONS}
              placeholder="Product Type"
              value={filters.product_type}
              onChange={(e) =>
                handleFilterChange("product_type", e.target.value)
              }
              isSearchable={false}
            /> */}
            <input
              className="form-control"
              placeholder={"Product Type"}
              value={filters.product_type}
              onChange={(e) =>
                handleFilterChange("product_type", e.target.value)
              }
            />
          </Col>
          <Col md={2}>
            <SearchableDropdown
              options={STATUS_OPTIONS}
              placeholder="Status"
              value={filters.status}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              isSearchable={false}
            />
          </Col>
          <Col md={4}>
            <Button
              onClick={handleSearch}
              disabled={!isAnyFilterApplied(filters)}
              className="filter-btn-gradient-primary btn-md responsive"
            >
              Search
            </Button>

            <Button
              // className="filter-btn-gradient-secondary btn-md responsive"
              className="secondary"
              disabled={!isAnyFilterApplied(filters)}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Col>
        </Row>
      </>
    );
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container className="mt--6" fluid>
        <Card>
          <ReactBsTable
            title="Product Types"
            description="Manage product types"
            data={data?.data}
            columns={columns}
            keyField="ID"
            addbtntext="Add Product Type"
            onAddBtnClick={() => {
              setSelected(null);
              setFormState({ values: { status: true }, errors: {} });
              setModalOpen(true);
            }}
            renderFilters={filterSection}
            showLoader={showLoader}
            page={pageNum}
            defaultSortField={sortConfig.dataField}
            defaultSortOrder={sortConfig.sortOrder}
            sizePerPage={10}
            totalRecords={data?.totalRecords}
            onPageChange={(p) => setPageNum(p)}
            onSortChange={handleSortChange}
          />
        </Card>
      </Container>

      <DynamicFormModal
        isOpen={modalOpen}
        toggle={() => setModalOpen(false)}
        title={isEditMode ? "Edit Product Type" : "Add Product Type"}
        fields={productTypeFormFields}
        formData={formState.values}
        errors={formState.errors}
        onChange={handleChange}
        onSubmit={handleSubmit}
        formState={formState}
        setFormState={setFormState}
        isEditMode={isEditMode}
        trueLabel={DEFAULT_STATUS_LABELS.active}
        falseLabel={DEFAULT_STATUS_LABELS.inactive}
        showLoader={showLoader}
      />
    </>
  );
};

export default ProductTypes;